import React from 'react';
import styled from 'styled-components';
import { lgPress, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { PaginationMeta } from '../types';
import GlassSelect from './GlassSelect';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  /* Soft glass strip — same family as the modals but at low strength so the
     table above it stays the visual focus. */
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  transition: border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 600px) {
    justify-content: center;
    padding: 10px 12px;
  }
`;

const Info = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.muted};

  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PageButton = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${(p) => (p.$active ? p.theme.green600 : p.theme.line)};
  border-radius: 8px;
  background: ${(p) => (p.$active ? p.theme.green600 : 'transparent')};
  color: ${(p) => (p.$active ? 'white' : p.theme.text)};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  transition: all 180ms cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$active ? p.theme.green600 : p.theme.buttonHover)};
    border-color: ${(p) => (p.$active ? p.theme.green600 : p.theme.borderHover)};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 400px) {
    min-width: 32px;
    height: 32px;
    font-size: 12px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.06' : '0.6')}), rgba(255,255,255,0) 60%);
`;

const PageSizeSelect = styled(GlassSelect)`
  margin-left: 12px;
  min-width: 100px;

  @media (max-width: 400px) {
    margin-left: 4px;
  }
`;

interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ pagination, onPageChange, onPageSizeChange }) => {
  const { page, pageSize, totalRows, totalPages } = pagination;

  const start = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  // Generate page numbers to show
  const getPages = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <Container>
      <Info>
        Showing {start.toLocaleString()}–{end.toLocaleString()} of {totalRows.toLocaleString()}
      </Info>
      <Controls>
        <PageButton disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <i className="fas fa-chevron-left" style={{ fontSize: 11 }}></i>
        </PageButton>
        {getPages().map((p, i) =>
          p === '...' ? (
            <PageButton key={`dots-${i}`} disabled style={{ border: 'none', background: 'none', opacity: 1 }}>
              ...
            </PageButton>
          ) : (
            <PageButton key={p} $active={p === page} onClick={() => onPageChange(p)}>
              {p}
            </PageButton>
          )
        )}
        <PageButton disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          <i className="fas fa-chevron-right" style={{ fontSize: 11 }}></i>
        </PageButton>
        <PageSizeSelect deferChange /* [FLUID-8 2026-10-12] instant pick */ value={String(pageSize)} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
          <option value="25">25 / page</option>
          <option value="50">50 / page</option>
          <option value="100">100 / page</option>
        </PageSizeSelect>
      </Controls>
    </Container>
  );
};

export default Pagination;
